import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import CommandsQueriesClient from "../../requests/queries/CommandsQueries";
import "./CurrentCartStyles.css";
import { convertColorToHex } from "../../utils/utils";

const CurrentCart = ({ currentCommand, setCurrentCommand }) => {
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [cartItems, setCartItems] = useState(currentCommand?.products || []);

  // Mettre à jour cartItems immédiatement quand currentCommand.products change
  useEffect(() => {
    if (currentCommand?.products && Array.isArray(currentCommand.products)) {
      console.log("CurrentCart - Mise à jour cartItems avec:", currentCommand.products);
      setCartItems(currentCommand.products);
    } else {
      setCartItems([]);
    }
  }, [currentCommand]);

  const [isLoading, setIsLoading] = useState(false);
  const lastCommandRef = useRef(null);
  const [error, setError] = useState(null);

  const getTotalItems = () => {
    if (!currentCommand?.products) return 0;
    return currentCommand.products.reduce((total, product) => {
      if (product.variants && product.variants.length > 0) {
        return total + product.variants.reduce((variantTotal, variant) => {
          return variantTotal + (variant.quantity || 0);
        }, 0);
      }
      return total + 1;
    }, 0);
  };

  const getTotalPrice = () => {
    if (!currentCommand?.products) return 0;
    return currentCommand.products.reduce((total, product) => {
      if (product.prices && product.prices.length > 0) {
        const price = product.prices.find(price => product.default_price_id === price.id)?.price || product.prices[0]?.price || 0;
        
        if (product.variants && product.variants.length > 0) {
          const variantsTotal = product.variants.reduce((variantTotal, variant) => {
            return variantTotal + (parseFloat(price) * (variant.quantity || 0));
          }, 0);
          return total + variantsTotal;
        }
        
        return total + parseFloat(price);
      }
      return total;
    }, 0);
  };

  const loadCartDetails = async (silent = false) => {
    if (!currentCommand?.id) return;
    
    if (!silent) setIsLoading(true);
    try {
      const res = await CommandsQueriesClient.findById(currentCommand.id);
      if (res && res.data) {
        setCurrentCommand(res.data.command);
        setCartItems(res.data.command.products || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement du panier:", error);
      setError(error.response?.data?.message || 'An error occurred');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const removeFromCart = async (variantId) => {
    if (!currentCommand?.id || !variantId) return;
    
    try {
      await CommandsQueriesClient.editProductsForCommand(currentCommand.id, variantId, 0);
      await loadCartDetails(true);
      setError(null);
    } catch (error) {
      console.error("Erreur lors de la suppression du produit:", error);
      setError(error.response?.data?.message || 'An error occurred');
    }
  };

  const updateQuantity = async (variantId, newQuantity) => {
    if (!currentCommand?.id || newQuantity < 0) return;
    
    try {
      await CommandsQueriesClient.editProductsForCommand(currentCommand.id, variantId, newQuantity);
      await loadCartDetails(true);
      setError(null);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la quantité:", error);
      setError(error.response?.data?.message || 'An error occurred');
    }
  };
  
  useEffect(() => {
    if (currentCommand?.id) {
      loadCartDetails(false);
    } else {
      setCartItems([]);
    }
  }, [currentCommand?.id]);

  // S'assurer que le panier est chargé/calculé quand on est sur la route /shop
  useEffect(() => {
    if (location.pathname === "/shop" && currentCommand?.id) {
      loadCartDetails(true);
    }
  }, [location.pathname, currentCommand?.id]);

  useEffect(() => {
    if (!currentCommand?.id || !isDropdownOpen) return;

    const commandKey = `${currentCommand.id}-${currentCommand.products?.length || 0}-${currentCommand.products?.reduce((sum, p) => sum + (p.variants?.length || 1), 0) || 0}`;

    if (lastCommandRef.current !== null && lastCommandRef.current !== commandKey && lastCommandRef.current.startsWith(`${currentCommand.id}-`)) {
      const timer = setTimeout(() => {
        loadCartDetails(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    
    lastCommandRef.current = commandKey;
  }, [isDropdownOpen, currentCommand]);

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();

  return (
    <div className="current-cart">
      <button 
        className="cart-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="Open cart"
      >
        <i className="bi bi-cart3"></i>
        <span className="cart-count">{totalItems}</span>
      </button>

      {isDropdownOpen && (
        <div className="cart-dropdown">
          <div className="cart-dropdown-header">
            <h3>My Cart</h3>
            <button 
              className="close-cart"
              onClick={() => setIsDropdownOpen(false)}
              aria-label="Close cart"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          <div className="cart-dropdown-content">
            {isLoading ? (
              <div className="cart-loading">
                <i className="bi bi-arrow-clockwise"></i>
                <span>Loading...</span>
              </div>
            ) : cartItems.length === 0 ? (
              <div className="cart-empty">
                <i className="bi bi-cart-x"></i>
                <p>Your cart is empty</p>
              </div>
            ) : (
              <div className="cart-items">
                {cartItems.map((product) => (
                  <div key={product.id} className="cart-item">
                    <div className="cart-item-image">
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0].image_url} 
                          alt={product.name}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/60x60?text=Image';
                          }}
                        />
                      ) : (
                        <div className="no-image">
                          <i className="bi bi-image"></i>
                        </div>
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h4 className="cart-item-name">{product.name}</h4>
                      
                      {product.variants && product.variants.length > 0 ? (
                        <div className="cart-item-variants">
                          {product.variants.map((variant) => (
                            <div key={variant.id} className="cart-item-variant">
                              <div className="variant-info">
                                <span className="variant-color">
                                  {variant.color && (
                                    <span 
                                      className="color-dot" 
                                      style={{backgroundColor: convertColorToHex(variant.color)}}
                                    ></span>
                                  )}
                                  {variant.color} {variant.size && `- ${variant.size}`}
                                </span>
                                <span className="variant-price">
                                  {product.prices && product.prices[0] ? 
                                    `${product.prices[0].price}€` : 'Price not available'
                                  }
                                </span>
                              </div>
                              
                              <div className="variant-controls">
                                <div className="quantity-controls">
                                  <button 
                                    className="quantity-btn"
                                    onClick={() => updateQuantity(variant.id, (variant.quantity || 0) - 1)}
                                    disabled={!variant.quantity || variant.quantity <= 0}
                                  >
                                    <i className="bi bi-dash"></i>
                                  </button>
                                  <span className="quantity">{variant.quantity || 0}</span>
                                  <button 
                                    className="quantity-btn"
                                    onClick={() => updateQuantity(variant.id, (variant.quantity || 0) + 1)}
                                  >
                                    <i className="bi bi-plus"></i>
                                  </button>
                                </div>
                                
                                <button 
                                  className="remove-btn"
                                  onClick={() => removeFromCart(variant.id)}
                                  title="Remove"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="cart-item-simple">
                          <span className="item-price">
                            {product.prices && product.prices[0] ? 
                              `${product.prices[0].price}€` : 'Price not available'
                            }
                          </span>
                          
                          <div className="cart-empty-message">
                            <p>This product requires a variant. Please use the product page to add it.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="cart-dropdown-footer">
              <div className="cart-total">
                <span className="total-label">Total:</span>
                <span className="total-price">{totalPrice.toFixed(2)}€</span>
              </div>
              <button className="checkout-btn" onClick={()=>{window.location.href = '/client/checkout'}}>
                <i className="bi bi-credit-card"></i>
                Checkout
              </button>
              {error && (
                <div className="cart-dropdown-error-message mt-3">
                  <div style={{width: '100%'}} className="alert alert-danger">
                    {error}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CurrentCart;
